import {matchPath} from "react-router-dom";

import {
    SPORT_PAGE,
    SPORT_ROOT_PATH,
    TIPS_AND_NEWS_ROUTE,
} from "@atg-sport-shared/sportsbook-utils-constants";

type Keys =
    | "articleId"
    | "sport"
    | "region"
    | "league"
    | "eventId"
    | "attribute"
    | "isTipsArticle"
    | "bethistoryId"
    | "bethistoryDate"
    | "bethistoryStatus"
    | "participant"
    | "isLiveEvent"
    | "filterType";

type Params = {[key in Keys]?: string};

type RouteType = {
    [key in string]?: string | ((params: Params) => string);
};

const isLongSport = ({region, league, attribute = ""}: Params): boolean =>
    region === "all" && league === "all" && ["matches"].includes(attribute);

const isSportPage = ({sport, region, league, attribute}: Params): boolean =>
    sport !== "all" && (!region || isLongSport({sport, region, league, attribute}));

const filterPage = ({
    sport,
    region,
    league,
    participant,
    attribute = "",
}: Params): string =>
    (isSportPage({sport, region, league, attribute}) && SPORT_PAGE.SPORT) ||
    (attribute === "in-play" && SPORT_PAGE.LIVE_NOW) ||
    (attribute?.includes("starting-soon") && SPORT_PAGE.STARTING_SOON) ||
    (attribute?.includes("streaming") && SPORT_PAGE.STREAMING) ||
    (participant &&
        participant !== "all" &&
        region === "all" &&
        SPORT_PAGE.PARTICIPANT) ||
    SPORT_PAGE.LEAGUE;

// https://www.atg.se/sport/ice_hockey#sports-hub/ice_hockey
//                                    ⬆
//                                    Kambi hash routes
const kambiHashes: RouteType = {
    "": SPORT_PAGE.LANDING,
    home: SPORT_PAGE.LANDING,
    nyheter: SPORT_PAGE.NEWS,
    "starting-soon": SPORT_PAGE.STARTING_SOON,
    "in-play": SPORT_PAGE.LIVE_NOW,
    "sports/a-z": SPORT_PAGE.LEAGUE,
    "nyheter/tips": SPORT_PAGE.TIPS,
    "artikel/:isTipsArticle?": SPORT_PAGE.ARTICLE,
    "bethistory/detail/:bethistoryId": SPORT_PAGE.BET_HISTORY_DETAIL,
    "bethistory/:bethistoryDate?/:bethistoryStatus?": SPORT_PAGE.BET_HISTORY,
    "bonus-offers": SPORT_PAGE.BONUS_OFFERS,
    "event/:isLiveEvent?/:eventId": SPORT_PAGE.EVENT,
    "event/:isLiveEvent?/:eventId/tab/*": SPORT_PAGE.EVENT,
    "popular-events": SPORT_PAGE.POPULAR_EVENTS,
    "filter/:sport/:region?/:league?/:participant?/:attribute?/*": filterPage, // Remove?
    "sports-hub/:sport/:region?/:league?/:participant?/:attribute?/*": filterPage,
    "participant/:id": SPORT_PAGE.PARTICIPANT,
    specials: SPORT_PAGE.SPECIALS,
    "coupon/:id": SPORT_PAGE.COUPON,
};

// Paths with Kambi functionality
const kambiPaths: RouteType = {
    [`${SPORT_ROOT_PATH}/big9/*`]: undefined,
    [`${TIPS_AND_NEWS_ROUTE}/*`]: undefined,
    [`${SPORT_ROOT_PATH}/nyheter/tips`]: SPORT_PAGE.TIPS, // Remove?
    [`${SPORT_ROOT_PATH}/nyheter/:articleId`]: SPORT_PAGE.ARTICLE, // Remove?
    [`${SPORT_ROOT_PATH}/nyheter`]: SPORT_PAGE.NEWS, // Remove?
    [`${SPORT_ROOT_PATH}/in-play`]: SPORT_PAGE.LIVE_NOW,
    [`${SPORT_ROOT_PATH}/starting-soon`]: SPORT_PAGE.STARTING_SOON,
    [`${SPORT_ROOT_PATH}/streaming`]: SPORT_PAGE.STREAMING,
    [`${SPORT_ROOT_PATH}/:sport/:region?/:league?`]: SPORT_PAGE.LEAGUE,
    [`${SPORT_ROOT_PATH}/:sport`]: SPORT_PAGE.SPORT,
    [`${SPORT_ROOT_PATH}`]: SPORT_PAGE.LANDING,
    "/konto/mina-spel/sport": SPORT_PAGE.BET_HISTORY, // remove when verticals are using new urls
    "/konto/mina-spel/sportbok": SPORT_PAGE.BET_HISTORY,
};

const getRoutePage = (
    match: ReturnType<typeof matchPath>,
    routes: RouteType,
): string | undefined => {
    if (!match) return undefined;
    const page = routes[match.pattern.path];

    if (typeof page === "function") return page(match.params);
    return page;
};

const matcherFactory = (routes: RouteType) => (pathPattern: string) => {
    const matchedPath =
        Object.keys(routes).find((path) => {
            if (pathPattern[0] !== "/") pathPattern = `/${pathPattern}`;

            return matchPath(path, pathPattern);
        }) || "";

    const match = matchPath(matchedPath, pathPattern);
    const page = getRoutePage(match, routes);
    return {...match?.params, page, raw: pathPattern};
};

export const kambiHashMatcher = matcherFactory(kambiHashes);
export const kambiPathMatcher = matcherFactory(kambiPaths);
